import formatClaimHistoryData, { type ClaimHistoryData, paid } from './utils/formatClaimHistoryData';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { isLocaleEnglish } from 'utils/languageUtils';
import { Row, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { type ClaimAmount, type ViewClaimFilters, useClaimHistoryDownload } from 'all-in-one-shared-api';
import getQueryValue from 'utils/getQueryValue';
import JSONCrush from 'jsoncrush';
import PrimaryButton from 'components/button/PrimaryButton';
import { StyledTitle } from 'components/StyledTitle';
import { useKeycloakReady } from 'hooks';
import { saveAs } from 'file-saver';

const ClaimHistoryPage = () => {
  const location = useLocation();
  const { state } = useLocation();
  const { claimsQuery } = state as { claimsQuery: ViewClaimFilters };
  const { i18n, t } = useTranslation();
  const { keycloak, ready } = useKeycloakReady();

  const [data, setData] = useState<ClaimHistoryData[]>([]);
  const dataTableRef = useRef(null);

  const columns: ColumnsType<ClaimHistoryData> = [
    {
      title: t('claimHistory.patientName'),
      dataIndex: 'patientName',
    },
    {
      title: t('claimHistory.claimId'),
      dataIndex: 'claimId',
      render: (value, record) => record.claimId ?? value,
    },
    {
      title: t('claimHistory.treatmentDate'),
      dataIndex: 'treatmentDate',
    },
    {
      title: t('claimHistory.claimType'),
      dataIndex: 'claimTypeDescription',
    },
    {
      title: t('claimHistory.receiptAmount'),
      dataIndex: 'receiptAmount',
      render: (value: ClaimAmount) => (value.currency && value.amount ? `${value.currency} ${value.amount}` : ''),
    },
    {
      title: t('claimHistory.paidAmount'),
      dataIndex: 'paidAmount',
      render: (value: paid) => (value.des ? `${value.des}` : `${value.currency} ${value.amount}`),
    },
    {
      title: t('claimHistory.shortfallAmount'),
      dataIndex: 'shortfallAmount',
      render: (value: ClaimAmount) => (value.currency && value.amount ? `${value.currency} ${value.amount}` : ''),
    },
    {
      title: t('claimHistory.reason'),
      dataIndex: 'reasons',
    },
    {
      title: t('claimHistory.paidDate'),
      dataIndex: 'paidDate',
    },
    {
      title: t('claimHistory.paymentMethod'),
      dataIndex: 'paymentMethod',
    },
  ];

  // Note(Andy Chow): The claimsQuery and claimStatuses here should be same as useViewClaimList's in FilterSection.tsx
  const { data: claimHistoryData } = useClaimHistoryDownload({
    accessToken: keycloak.token ?? '',
    filters: {
      ...claimsQuery,
      claimStatuses: ['COMPLETED', 'DECLINED', 'NEED_ACTION'],
    },
    stop: !ready,
    lang: i18n.language,
  });

  useEffect(() => {
    console.log(claimsQuery);
    const encodedData = getQueryValue(location.search)('data');
    if (encodedData) {
      const { data: historyData } = JSON.parse(decodeURIComponent(JSONCrush.uncrush(encodedData)));
      if (historyData?.length > 0) {
        historyData.map((item: ClaimHistoryData) => {
          if (item.networkClaim === 'Y') {
            item.paidAmount = isLocaleEnglish(i18n.language) ? { des: 'Network Services' } : { des: '網絡醫療服務' };
          }
        });

        setData(historyData.map(formatClaimHistoryData(isLocaleEnglish(i18n.language))));
      }
    }
  }, [location.search, i18n.language, claimsQuery]);

  const handleSaveAsPdf = async () => {
    if (claimHistoryData) {
      const file = new File([claimHistoryData as unknown as BlobPart], 'claimsHistoryReport.pdf', {
        type: 'application/pdf',
      });
      saveAs(file);
    }
  };
  return (
    <>
      <PrimaryButton onClick={handleSaveAsPdf} disabled={data?.length === 0} style={{ marginBottom: 12 }}>
        {t('claimHistory.saveAsPdf')}
      </PrimaryButton>
      <div ref={dataTableRef}>
        <Row justify="center">
          <StyledTitle level={4}>{t('claimHistory.title')}</StyledTitle>
        </Row>
        <Space direction="vertical">
          {data?.length > 0 ? (
            <Table
              bordered
              rowKey={(record) => (record.claimId ?? '') + record.receiptAmount?.amount}
              columns={columns}
              dataSource={data}
              pagination={false}
            />
          ) : (
            <Space align="center" direction="vertical">
              {t('claimHistory.noData')}
            </Space>
          )}
        </Space>
      </div>
    </>
  );
};

export default ClaimHistoryPage;
